/*
* @Author: Alexander.Vangelov@vonage.com
* @Date: 2020-07-01 15:06:30
 * @Last Modified by: Alexander.Vangelov@vonage.com
 * @Last Modified time: 2024-05-21 11:22:09
*/

import { share } from '../../c2d/share';
import { core, IDialerEvent_Logout, IDialerEvent_ScreenPop, IEntityRef } from '../../core';
import { getQueryParam } from '../../utils/getQueryParam';

require('./dialer.scss');

const DEFAULT_CC_DOMAIN = 'nam.newvoicemedia.com';

declare const vgVersion: any;

export interface IFeatures {
  contactsProvider?: boolean;
  openContact?: boolean;
  openActivity?: string;
  eventsHistory?: boolean;
  serviceWorker?: string;
}

export class CommonDialer {
  frame: HTMLIFrameElement;
  loadPromise: Promise<any>;
  ccDomain: string;
  ccAccount: string;
  ccEnv: string;
  ccCtiModule: string;
  provider: 'uc' | 'cc';
  constructor(
    provider: 'uc'|'cc' = 'uc', private standalone = true, private features: IFeatures = {}, 
    ccDomain = DEFAULT_CC_DOMAIN, ccAccount?: string
  ) {
    // this.standalone = standalone;
    this.provider = provider;
    this.frame = document.createElement('iframe');
    this.frame.allow = 'camera *; geolocation *; microphone *; autoplay *; hid: *; compute-pressure: *';
    this.frame.className = 'vgip-dialer';
    this.ccDomain = getQueryParam('cc_domain') || ccDomain;
    this.ccAccount = getQueryParam('cc_account') || ccAccount;
    this.ccCtiModule = getQueryParam('integration')
    if (provider === 'cc') {
      this.ccEnv = this.ccDomain.replace(/\..*/, '');
      // console.log('cc', this.ccDomain, this.ccEnv);
      this.debugLog('CC domain | environment', `${this.ccDomain} | ${this.ccEnv}`);
    }
    this.loadPromise = new Promise((resolve, reject) => {
      return this.frame.addEventListener('load', () => {
        resolve(this);
      }, true);
    });
    this.loadInterface(provider);
    setTimeout(() => {
      this.debugLog('Common CTI Layer', vgVersion);
      this.debugLog('Loading IFrame with URL:', this.frame.src);
    });
  }

  loadInterface = (provider: 'uc' | 'cc', ccDomain?: string, ccAccount?: string) => {
    if (provider === 'uc') {
      this.setupUCDialer();
      let dialerUrl = 'https://apps.gunify.vonage.com/cti/dialer';
      if (/^https:\/\/apps\.gunify/.test(location.origin)) { // load corresponding dialer in lower envs, but not on 3rd party domains
        dialerUrl = `${location.origin}/cti/dialer`;
      } else {
        switch((window as any).vgisEnv) {
          case 'dev': {
            dialerUrl = 'https://apps.gunify-dev.vocal-dev.com/cti/dialer'
            break;
          }
          case 'qa': {
            dialerUrl = 'https://apps.gunify-qa.vocal-qa.com/cti/dialer'
            break;
          }
          case 'staging': {
            dialerUrl = 'https://apps.gunify-staging.vocal-qa.com/cti/dialer'
            break;
          }
          case 'qa7': {
            dialerUrl = 'https://apps.gunify-qa7.vocal-qa.com/cti/dialer'
            break;
          }
        }
      }
      this.frame.src = dialerUrl;
      // this.frame.src = '/cti/dialer'; // to run on localhost (webpack)
      // this.frame.src = 'http://localhost:4202/cti/dialer'; // test local with http-server
      // this.frame.src = 'https://apps.gunify-dev.vocal-dev.com/cti/dialer';
    } else {
      this.setupCCDialer();
      let iframeUrl = `https://${ccDomain || this.ccDomain}/CallCentre/servicecloud/agentinterface?crm=sdk&topDomain=${location.origin}`;
      if (ccAccount || this.ccAccount) {
        iframeUrl += `&account=${encodeURIComponent(ccAccount || this.ccAccount)}`
      }
      if (this.ccCtiModule) {
        iframeUrl += `&ctiModule=${encodeURIComponent(this.ccCtiModule)}`;
      }
      this.frame.src = iframeUrl;
    }
  }

  debugLog = (...args: any[]) => {
    const playgroundLog = (window as any).playgroundLog;
    if (playgroundLog) {
      playgroundLog(...args);
    }
  }

  positionStandalone(top, left) {
    if (top < 455) {
      this.frame.style.bottom = 'initial';
      this.frame.style.top = '-15px'
    } else {
      this.frame.style.top = 'initial';
      this.frame.style.bottom = '-15px';
    }
    if (left < 275) {
      this.frame.style.right = 'initial';
      this.frame.style.left = '-15px';
    } else {
      this.frame.style.left = 'initial';
      this.frame.style.right = '-15px';
    }
    this.debugLog('Set dialer position (standalone)');
  }

  setVisibility = (visible: boolean) => {
    if (this.standalone) {
      this.frame.style.display = visible ? 'block' : 'none';
      this.debugLog('Set dialer visibility', visible.toString());
      if (visible) {
        const clickOut = () => {
          document.removeEventListener('click', clickOut);
          this.setVisibility(false);
          if (this.frame.parentElement.className === 'vgip-button active') {
            this.frame.parentElement.classList.remove('active');
          }
        }
        document.addEventListener('click', clickOut);
      }
    }
  }

  focus() {
    if (this.frame.contentWindow) {
      this.frame.contentWindow.focus();
    }
    this.debugLog('Set dialer focus');
  }

  normalizeCCInteractionEvent(rawEvent) {
    if (
      ['interactionStart', 'interactionEnd', 'interactionAnswered'].indexOf(rawEvent.actionType) === -1 ||
      !rawEvent.data || !rawEvent.data.agentId || !rawEvent.data.interactionGuid || 
      ['call', 'chat', 'email'].indexOf(rawEvent.data.type) === -1 ||
      !rawEvent.data.contactWorldNumber || !rawEvent.data.customerNumber ||
      ['outbound', 'inbound', 'internal'].indexOf(rawEvent.data.direction) === -1
    ) {
      this.debugLog(`Unknown or invalid CC event ${JSON.stringify(rawEvent)}`);
      return;
    }
    if (
      rawEvent.actionType === 'interactionEnd' && 
      (typeof(rawEvent.data.duration) !== 'number' || typeof(rawEvent.data.connected) !== 'boolean')
    ) {
      this.debugLog(`Invalid CC end event ${JSON.stringify(rawEvent)}`);
      return;
    }
    const isOutbound = rawEvent.data.direction.toUpperCase() === 'OUTBOUND'
    const dialedNumber = isOutbound ? rawEvent.data.customerNumber : rawEvent.data.contactWorldNumber;
    const iEvent: any = {
      data: {
        id: rawEvent.data.interactionGuid,
        uid: `vrn:vcc:${this.ccEnv}:${(rawEvent.data.type || 'call').toLowerCase()}:${rawEvent.data.interactionGuid}`,
        direction: (rawEvent.data.direction || 'outbound').toUpperCase(),
        internal: rawEvent.data.direction === 'internal',
        startDate: rawEvent.data.startedAt ? new Date(rawEvent.data.startedAt) : new Date(),
        type: ((rawEvent.data.type === 'email') ? 'CHAT' : (rawEvent.data.type || 'CALL')).toUpperCase(), // VGIS-6850
        vcc: {
          vonageccguid: rawEvent.data.interactionGuid,
          vonageagentid: rawEvent.data.agentId
        }
      }
    };
    if (rawEvent.data.customerNumber) {
      iEvent.data.vcc.customernumber = rawEvent.data.customerNumber;
    }
    if (rawEvent.data.contactWorldNumber) {
      iEvent.data.vcc.contactWorldNumber = rawEvent.data.contactWorldNumber;
    }
    if (rawEvent.data.type === 'call') {
      iEvent.data.phoneNumber = rawEvent.data.customerNumber;
      iEvent.data.did = rawEvent.data.contactWorldNumber;
      iEvent.data.vcc.callrecording = `https://${this.ccDomain}/CallCentre/callAnalytics/Call/${rawEvent.data.interactionGuid}`;
      iEvent.data.vcc.dialednumber = dialedNumber;
      iEvent.data.vcc.customernumber = rawEvent.data.customerNumber;
      iEvent.data.vcc.callduration = (rawEvent.data.connected ? Math.round((rawEvent.data.duration || 1) / 1000) : 0).toString();
      iEvent.data.vcc.answered = (rawEvent.actionType === 'interactionAnswered' || rawEvent.data.connected) ? true : false;
      if (rawEvent.actionType === 'interactionStart') {
        iEvent.type = 'CALL_START';
        iEvent.data.state = 'RINGING';
      } else if (rawEvent.actionType === 'interactionAnswered') {
        iEvent.type = 'CALL_ANSWER';
        iEvent.data.state = 'ACTIVE';
        iEvent.data.duration = rawEvent.data.duration || 0;
      } else {
        iEvent.type = 'CALL_END';
        if (rawEvent.data.connected) {
          iEvent.data.state = 'ANSWERED';
        } else {
          iEvent.data.state = rawEvent.data.direction.toUpperCase() === 'INBOUND' ? 'REJECTED' : 'CANCELLED'; 
        }
        iEvent.data.duration = rawEvent.data.duration || 0;
        if (rawEvent.actionType === 'interactionEnd') {
          iEvent.data.endDate = new Date(rawEvent.data.startedAt + iEvent.data.duration);
        }
      }
    } else if (['chat', 'email'].indexOf(rawEvent.data.type) !== -1) {
      iEvent.type = rawEvent.actionType === 'interactionStart' ? 'CHAT_START' : 'CHAT_END';
      if (rawEvent.actionType === 'interactionEnd') {
        iEvent.data.duration = rawEvent.data.duration || 0;
        iEvent.data.endDate = new Date(rawEvent.data.startedAt + iEvent.data.duration);
      }
    }
    if (rawEvent.data.crmData) {
      if (rawEvent.data.crmData.ClickToDialData) {
        iEvent.data.c2d = rawEvent.data.crmData.ClickToDialData;
        delete rawEvent.data.crmData.ClickToDialData;
      }
      if (rawEvent.data.crmData.formData) {
        iEvent.data.formData = rawEvent.data.crmData.formData;
        delete rawEvent.data.crmData.formData;
      }
      let refId: string, refType: string, refIntegration: string, refExtra: {[key: string]: any} = {};
      for (const refKey of Object.keys(rawEvent.data.crmData)) {
        switch ((refKey || '').toLowerCase()) {
          case 'entityid':
          case 'id': {
            refId = rawEvent.data.crmData[refKey];
            break;
          } 
          case 'entitytype':
          case 'entity': {
            refType = rawEvent.data.crmData[refKey];
            break;
          }
          case 'integration': {
            refIntegration = rawEvent.data.crmData[refKey];
            break;
          }
          default: {
            refExtra[refKey] = rawEvent.data.crmData[refKey];
          }
        }
      }
      if (refId && refType) {
        iEvent.data.contact = {
          id: refId,
          type: refType,
          provider: refIntegration || ''
        }
        if (Object.keys(refExtra).length) {
          iEvent.data.contact.extraAttributes = refExtra;
        }
      }
    }
    return iEvent;
  }

  setupCCDialer() {
    window.addEventListener('message', (event) => {
      if (event.origin !== location.origin) {
        if (event.data && event.data.actionType) {

          //test attach fake data
          // event.data.data.type = 'chat';
          // event.data.data.crmData = event.data.data.crmData || {};
          // event.data.data.crmData.EntityID = '79d1271a-2fe7-e611-80f4-e0071b661f01', //'dc9e62a8-90df-e311-9565-a45d36fc5fe8'
          // event.data.data.crmData.Entity = 'incident'

          this.debugLog('CC RAW Event:', event.data);
          if (['interactionStart', 'interactionEnd', 'interactionAnswered'].indexOf(event.data.actionType) !== -1) {
            const interactionEvent = this.normalizeCCInteractionEvent(event.data);
            if (interactionEvent) {
              this.debugLog('InteractionEvent', interactionEvent);
              if (typeof(core.onInteractionEvent) === 'function') {
                core.onInteractionEvent(interactionEvent);
              }
              if (event.data.actionType === 'interactionStart' && event.data.data.crmData) {
                const crmData = event.data.data.crmData;
                const entityId = (crmData.EntityID || crmData.Entity || crmData.entityId || '');
                const entityType = (crmData.EntityType || crmData.Entity || crmData.entityType || '');
                const integration = (crmData.integration || crmData.Integration || '').toUpperCase();
                if ((entityId && entityType && integration === 'WEBLAUNCH')) {
                  const iEvent: IDialerEvent_ScreenPop = {
                    type: 'SCREEN_POP',
                    data: {
                      id: entityId,
                      type: entityType,
                      provider: integration
                    }
                  }
                  this.debugLog('ScreenPopExternal', iEvent);
                  this.openWeblaunchPopup(iEvent);
                }
              }
              if (/_START$/.test(interactionEvent.type) && sessionStorage.getItem('eid')) {
                window.postMessage({ scMsgReq: { action: 'setMyWindowProps', data: { f: true } } }, location.href);
              }
            } else {
              this.debugLog('Does not appear to be a valid interaction event');
            }
          } else if (event.data.actionType === 'contactPadLoad') {
            const iEvent: any = {
              type: 'READY',
              data: event.data.data || {}
            }
            this.debugLog('InteractionEvent', iEvent);
            if (typeof(core.onInteractionEvent) === 'function') {
              core.onInteractionEvent(iEvent);
            }
            iEvent.type = 'HEALTH';
            iEvent.data = true;
            this.debugLog('InteractionEvent', iEvent);
            if (typeof(core.onInteractionEvent) === 'function') {
              core.onInteractionEvent(iEvent);
            }
          } else if (event.data.actionType === 'contactPadUnload') {
            const iEvent: IDialerEvent_Logout = {
              type: 'LOGOUT',
              data: event.data.data || {}
            }
            this.debugLog('InteractionEvent', iEvent);
            if (typeof(core.onInteractionEvent) === 'function') {
              core.onInteractionEvent(iEvent);
            }
          } else if (event.data.actionType === 'screenPopExternal' && event.data.data) {
            const iEvent: IDialerEvent_ScreenPop = {
              type: 'SCREEN_POP',
              data: {
                id: event.data.data.entityId,
                type: event.data.data.entityType || event.data.data.entityTypeId,
                provider: event.data.data.integration || event.data.data.provider
              }
            }
            this.debugLog('ScreenPopExternal', iEvent);
            if ((iEvent.data.provider || '').toUpperCase() === 'WEBLAUNCH') {
              this.openWeblaunchPopup(iEvent);
            } else if (typeof(core.onInteractionEvent) === 'function') {
              core.onInteractionEvent(iEvent);
            }
          } else {
            this.debugLog('Does not appear to be a valid event', event.data);
          }
        // } else {
        //   this.debugLog('Does not appear to be a valid event', event.data);
        }
      }
    });
    // (window as any).addEventListener('Nvm.interactionStarted', ({ detail }) => {
    //   console.log('ContactPad CTI: Interaction Started Event fired', detail);
    // })
    // (window as any).addEventListener('Nvm.interactionEnded', ({ detail }) => {
    //   console.log('ContactPad CTI: Interaction Ended Event fired', detail);
    // })
    // (window as any).addEventListener('Nvm.interactionRetrieved', ({ detail }) => {
    //   console.log('ContactPad CTI: Interaction Retrieved Event fired', detail)
    // })
    this.debugLog('Setup CC Dialer')
  }

  setupUCDialer() {
    this.debugLog('Setup UC Dialer');
    this.loadPromise.then(() => {
      const onPortMessage = ({ data }) => {
        if (data.type) {
          this.debugLog('InteractionEvent', data);
          if (typeof(core.onInteractionEvent) === 'function') {
            core.onInteractionEvent(data);
          }
          if (/_START$/.test(data.type) && sessionStorage.getItem('eid')) {
            window.postMessage({ scMsgReq: { action: 'setMyWindowProps', data: { f: true } } }, location.href);
          }
          if (data.type === 'LOGOUT') {
            this.setVisibility(true); // force show when user is not logged in
            delete share.c2sms;
          }
        } else if (data.action) {
          switch (data.action) {
            case 'searchExternalContacts': {
              const replyCallback = (contacts) => {
                this.frame.contentWindow.postMessage(
                  { action: 'autocompleteContacts', contacts }, '*', [new MessageChannel().port2]
                );
              };
              if (typeof(core.onSearchContactables) === 'function') {
                core.onSearchContactables(data.q, replyCallback);
              } else {
                replyCallback([]);
              }
              break;
            }
            case 'openExternalContact': {
              if (typeof(core.onOpenContactable) === 'function') {
                core.onOpenContactable(data.contact);
              }
              break;
            }
            case 'settingsChanged': {
              if (data.settings?.sdkSms) {
                share.c2sms = true;
              }
              break;
            }
            default: {
              this.debugLog('UC Action Event:', data);
            }
          }
        } else {
          this.debugLog('UC RAW Event:', data);
        }
      };
      const messageChannel = new MessageChannel();
      messageChannel.port1.addEventListener('message', onPortMessage, false);
      messageChannel.port1.start();
      // console.log('features subscribe', this.features);
      this.frame.contentWindow.postMessage({ 
        action: 'subscribe', 
        features: {
          contactsProvider: this.features ? this.features.contactsProvider : false,
          openContact: this.features ? this.features.openContact : false,
          openActivity: this.features ? this.features.openActivity : null,
          eventsHistory: this.features ? this.features.eventsHistory : true,
          serviceWorker: this.features ? this.features.serviceWorker : null,
        } 
      }, '*', [messageChannel.port2]);
    }, (error) => {
      this.debugLog('UC Dialer ERROR:', error);
    });
  }

  placeCall = (phoneNumber: string, entityRef?: IEntityRef): Promise<string> => {
    const normalizedPhoneNumber = (phoneNumber || '').replace(/[^+*\d]/g, '');
    return new Promise<string>((resolve, reject) => {
      if (this.provider == 'cc') {
        if (location.protocol !== 'https:') {
          const error = 'Integration with the CC provider requires the integration page to be served over HTTPS'
          reject(error);
          this.debugLog(error);
        } else {
          let timeoutId = null;
          const c2dMsg = {
            actionType: 'makeCall',
            data: {
              phoneNumber: normalizedPhoneNumber,
              crmData: entityRef || {}
            }
          }
          // to match the corresponding event by phone number, strip non digits and the leading zeros
          const phoneMatchRegex = new RegExp(`${normalizedPhoneNumber.replace(/^\+/, '\\+').replace(/^0+/, '')}$`);
          const onTempCCMessageListener = ({ data }) => {
            if (data.actionType === 'interactionStart' && phoneMatchRegex.test(data.data?.customerNumber || '')) {
              window.removeEventListener('message', onTempCCMessageListener);
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
              resolve(data.data?.interactionGuid);
              this.debugLog('Place call success', `Expecting events with id: "${data.data?.interactionGuid}"`);
            }
          }
          const onTimeout = () => {
            timeoutId = null;
            window.removeEventListener('message', onTempCCMessageListener);
            const error = 'Error: No response received from the CC dialer';
            reject(error);
            this.debugLog('Place call failed', error);
          };
          window.addEventListener('message', onTempCCMessageListener);
          timeoutId = setTimeout(onTimeout, 2000);
          this.frame.contentWindow.postMessage(c2dMsg, '*');
          this.debugLog('Sending VCC CTI SDK message:', JSON.stringify(c2dMsg));
        }
      } else {
        let timeoutId = null;
        const messageChannel = new MessageChannel();
        const onPortMessage = ({ data }) => {
          messageChannel.port1.removeEventListener('message', onPortMessage, false);
          messageChannel.port1.close();
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          if (data.error) {
            reject(data.error);
            this.debugLog('Place call failed', `Error: ${data.error}`);
          } else {
            resolve(data.id);
            if (data.id) {
              this.debugLog('Place call success', `Expecting events with id: "${data.id}"`);
            } else {
              this.debugLog('Place call success', 'Missing call id');
            }
          }
        };
        const onTimeout = () => {
          timeoutId = null;
          messageChannel.port1.removeEventListener('message', onPortMessage);
          messageChannel.port1.close();
          const error = 'Error: No response received from the UC dialer';
          reject(error);
          this.debugLog('Place call failed', error);
        };
        messageChannel.port1.addEventListener('message', onPortMessage, false);
        messageChannel.port1.start();
        timeoutId = setTimeout(onTimeout, 6000);
        this.frame.contentWindow.postMessage({ action: 'placeCall', data: normalizedPhoneNumber, entityRef }, '*', [messageChannel.port2]);
        this.debugLog('Sending placing call request:', { phoneNumber, entityRef });
      }
      // this.debugLog('Placing call:', phoneNumber);
      this.setVisibility(true);
    });
  }

  sendSms = (phoneNumber: string, text?: string, entityRef?: IEntityRef): Promise<string> => {
    const normalizedPhoneNumber = (phoneNumber || '').replace(/[^+*\d]/g, '');
    return new Promise<string>((resolve, reject) => {
      if (this.provider === 'uc') {
        if (share.c2sms) {
          let timeoutId = null;
          const messageChannel = new MessageChannel();
          const onPortMessage = ({ data }) => {
            messageChannel.port1.removeEventListener('message', onPortMessage, false);
            messageChannel.port1.close();
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            if (data.error) {
              reject(data.error);
              this.debugLog('Send SMS failed', `Error: ${data.error}`);
            } else {
              resolve(data.id);
              if (data.id) {
                this.debugLog('Send SMS success', `Expecting events with id: "${data.id}"`);
              } else {
                this.debugLog('Send SMS success', 'No message');
              }
            }
          };
          const onTimeout = () => {
            timeoutId = null;
            messageChannel.port1.removeEventListener('message', onPortMessage);
            messageChannel.port1.close();
            const error = 'Error: No response received from dialer';
            reject(error);
            this.debugLog('Send SMS failed', error);
          };
          messageChannel.port1.addEventListener('message', onPortMessage, false);
          messageChannel.port1.start();
          timeoutId = setTimeout(onTimeout, 6000);
          this.frame.contentWindow.postMessage(
            { action: 'sendSms', data: normalizedPhoneNumber, entityRef, text }, '*', [messageChannel.port2]
          );
          this.debugLog('Sending SMS request:', { phoneNumber, text, entityRef });
        } else {
          const error = 'Error: No user logged in or programmatic sending of SMS is not allowed.';
          reject(error);
          this.debugLog('Send SMS failed', error);
        }
        this.setVisibility(true);
      } else {
        const error = `Error: SMS is not supported by the current provider (${this.provider})`;
        reject(error);
        this.debugLog('Send SMS failed', error);
      }
    });
  }

  closeSms = (phoneNumber?: string): Promise<string> => {
    const normalizedPhoneNumber = (phoneNumber || '').replace(/[^+*\d]/g, '');
    return new Promise<string>((resolve, reject) => {
      if (this.provider === 'uc') {
        if (share.c2sms) {
          let timeoutId = null;
          const messageChannel = new MessageChannel();
          const onPortMessage = ({ data }) => {
            messageChannel.port1.removeEventListener('message', onPortMessage, false);
            messageChannel.port1.close();
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            if (data.error) {
              reject(data.error);
              this.debugLog('Close SMS conversation failed', `Error: ${data.error}`);
            } else {
              resolve(data.id);
              this.debugLog(normalizedPhoneNumber ? `Close SMS conversation with "${normalizedPhoneNumber}" success` : 'Closing all SMS conversations success');
            }
          };
          const onTimeout = () => {
            timeoutId = null;
            messageChannel.port1.removeEventListener('message', onPortMessage);
            messageChannel.port1.close();
            const error = 'Error: No response received from dialer';
            reject(error);
            this.debugLog('Close SMS conversation failed', error);
          };
          messageChannel.port1.addEventListener('message', onPortMessage, false);
          messageChannel.port1.start();
          timeoutId = setTimeout(onTimeout, 2000);
          this.frame.contentWindow.postMessage({ action: 'closeSms', data: normalizedPhoneNumber }, '*', [messageChannel.port2]);
          this.debugLog('Sending close SMS conversation request:', { phoneNumber });
        } else {
          reject('No user logged in or programmatic SMS is not allowed.');
        }
        this.setVisibility(false);
      } else {
        reject(`SMS is not supported by the current provider (${this.provider})`);
      }
    });
  }


  openWeblaunchPopup = (iEvent: IDialerEvent_ScreenPop) => {
    if ((iEvent.data.provider || '').toUpperCase() === 'WEBLAUNCH') {
      const state = `sdk_${new Date().getTime()}`;
      if (sessionStorage.getItem('eid')) {
        const tmo = setTimeout(() => {
          window.removeEventListener('message', onMsgResponse);
          this.debugLog('ScreenPopExternal open fallback (no browser extension)');
          if (iEvent.data.type === 'normal') {
            window.open(iEvent.data.id, '_blank');
          } else {
            window.open(iEvent.data.id, `cti_${new Date().getTime()}`, `width=890,height=608,left=${(screen.width / 2) - 445},top=${(screen.height / 2) - 304}`);
          }
        }, 1000);
        const onMsgResponse = (e) => {
          if (e.data && e.data.scMsgResp && e.data.state === state) {
            window.removeEventListener('message', onMsgResponse);
            this.debugLog('ScreenPopExternal result', e.data.scMsgResp);
            clearTimeout(tmo);
          }
        };
        window.addEventListener('message', onMsgResponse); 
        window.postMessage({ 
          scMsgReq: { action: 'openPopup', pageUrl: iEvent.data.id, options: { type: iEvent.data.type } 
        }, state: state }, location.origin);
      }
    } else {
      this.debugLog('ScreenPopExternal open without browser extension');
      if (iEvent.data.type === 'normal') {
        window.open(iEvent.data.id, '_blank');
      } else {
        window.open(iEvent.data.id, `cti_${new Date().getTime()}`, `width=890,height=608,left=${(screen.width / 2) - 445},top=${(screen.height / 2) - 304}`);
      }
    }
  }
}
